"use client";

import { useEffect, useMemo, useRef, useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import DoctorCard from "../DoctorCard/DoctorCard";
import SearchBarGroup from "../../../DoctorFinder/components/FindDoctorPage/SearchBarGroup/SearchBarGroup";
import "./ProvidersList.css";

import { resolveDoctorAvatar } from "../../../ProfileDoctor/hooks/useDoctorInformation";

const INITIAL = 6;
const STEP = 3;
const FILL_MAX_ITER = 12;

const pickDocAnyId = (doc) =>
  doc?.externalId ??
  doc?.doctorId ??
  doc?.medecinId ??
  doc?.id ??
  doc?._id ??
  null;

/**
 * Normalize a string for comparison:
 * lowercase + remove accents + trim
 */
const norm = (str) =>
  String(str ?? "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, " ")
    .trim();

const ALL_SPECIALTIES_VALUE = "All specialties";

/**
 * Return true if `haystack` contains `needle` (both already norm'd).
 * Empty needle always matches.
 */
const contains = (haystack, needle) => !needle || norm(haystack).includes(needle);

/**
 * Split normalized query into tokens
 */
const splitTokens = (value) => norm(value).split(" ").filter(Boolean);

/**
 * Check that every token exists somewhere in target
 */
const containsAllTokens = (target, query) => {
  const hay = norm(target);
  const tokens = splitTokens(query);
  if (!tokens.length) return true;
  return tokens.every((token) => hay.includes(token));
};

/**
 * Check if a doctor is verified/approved
 */
const isDoctorVerified = (doc) => {
  const status = String(doc?.status ?? "").toLowerCase().trim();
  const validationStatus = String(doc?.validationStatus ?? "").toLowerCase().trim();
  const accountStatus = String(doc?.accountStatus ?? "").toLowerCase().trim();
  const isVerified = doc?.isVerified ?? doc?.verified ?? doc?.isActive;

  // Vérifier si le statut est "approved" OU "confirmed"
  return (
    status === "approved" ||
    status === "confirmed" ||
    validationStatus === "approved" ||
    validationStatus === "confirmed" ||
    accountStatus === "approved" ||
    accountStatus === "confirmed" ||
    isVerified === true ||
    isVerified === "true" ||
    isVerified === 1
  );
};

export default function ProvidersList({ doctors = [] }) {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");

  const [searchQuery, setSearchQuery] = useState("");
  const [location, setLocation] = useState("");
  const [specialty, setSpecialty] = useState(ALL_SPECIALTIES_VALUE);

  const [applied, setApplied] = useState({
    q: "",
    loc: "",
    spec: "",
  });

  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
  }, [isRTL]);

  const normalized = useMemo(() => {
    const d = doctors;
    if (Array.isArray(d)) return d;
    if (!d) return [];
    if (Array.isArray(d.items)) return d.items;
    if (Array.isArray(d.data)) return d.data;
    if (Array.isArray(d.results)) return d.results;
    if (d instanceof Set) return Array.from(d);
    if (d instanceof Map) return Array.from(d.values());
    if (typeof d === "object") return Object.values(d);
    return [];
  }, [doctors]);

  // ✅ FILTRER UNIQUEMENT LES MÉDECINS VÉRIFIÉS
  const verifiedOnly = useMemo(() => {
    // 🔍 DEBUG TEMPORAIRE : à retirer une fois le bug identifié
    normalized.forEach((doc, i) => {
    });

    const filtered = normalized.filter(isDoctorVerified);
    return filtered;
  }, [normalized]);

  const enriched = useMemo(
    () =>
      verifiedOnly.map((doc) => {
        const raw = doc?.avatarUrl ?? doc?.avatar ?? doc?.photo ?? doc?.image ?? null;
        const resolved = raw ? resolveDoctorAvatar(raw) : null;
        return { ...doc, avatarResolved: resolved, avatarUrl: resolved || doc?.avatarUrl };
      }),
    [verifiedOnly]
  );

  const filtered = useMemo(() => {
    const { q, loc, spec } = applied;

    if (!q && !loc && !spec) return enriched;

    return enriched.filter((doc) => {
      const firstName = doc?.firstName ?? "";
      const lastName = doc?.lastName ?? "";
      const fullName = doc?.fullName ?? "";
      const simpleName = doc?.name ?? "";

      const fullNameForward = `${firstName} ${lastName}`.trim();
      const fullNameReverse = `${lastName} ${firstName}`.trim();

      const matchName = (() => {
        if (!q) return true;

        return (
          contains(fullName, q) ||
          contains(simpleName, q) ||
          contains(firstName, q) ||
          contains(lastName, q) ||
          contains(fullNameForward, q) ||
          contains(fullNameReverse, q) ||
          contains(doc?.clinicName, q) ||
          contains(doc?.practicePlace, q) ||

          containsAllTokens(fullName, q) ||
          containsAllTokens(simpleName, q) ||
          containsAllTokens(fullNameForward, q) ||
          containsAllTokens(fullNameReverse, q)
        );
      })();

      const matchLoc = (() => {
        if (!loc) return true;
        return (
          contains(doc?.city, loc) ||
          contains(doc?.country, loc) ||
          contains(doc?.clinicAddress, loc) ||
          contains(doc?.practiceAddress, loc) ||
          contains(doc?.address, loc) ||
          contains(doc?.location, loc)
        );
      })();

      const matchSpec = (() => {
        if (!spec) return true;
        return (
          contains(doc?.specialty, spec) ||
          contains(doc?.specialization, spec) ||
          contains(doc?.specialite, spec)
        );
      })();

      return matchName && matchLoc && matchSpec;
    });
  }, [enriched, applied]);

  const [count, setCount] = useState(INITIAL);
  const [waiting, setWaiting] = useState(false);

  useEffect(() => {
    setCount(Math.min(INITIAL, filtered.length));
  }, [filtered]);

  const gridRef = useRef(null);
  const sentinelRef = useRef(null);

  const visible = useMemo(() => filtered.slice(0, count), [filtered, count]);
  const remaining = Math.max(filtered.length - count, 0);

  const loadMore = useCallback(() => {
    if (waiting || remaining <= 0) return;
    setWaiting(true);
    const id = setTimeout(() => {
      setCount((c) => Math.min(c + STEP, filtered.length));
      setWaiting(false);
    }, 250);
    return () => clearTimeout(id);
  }, [waiting, remaining, filtered.length]);

  useEffect(() => {
    const el = sentinelRef.current;
    const grid = gridRef.current;
    if (!el || !grid || !("IntersectionObserver" in window)) return;

    const io = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting && remaining > 0 && !waiting) loadMore();
      },
      { root: grid, rootMargin: "80px", threshold: 0.01 }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [remaining, waiting, loadMore]);

  useEffect(() => {
    const grid = gridRef.current;
    if (!grid) return;
    let iter = 0;

    const fill = () => {
      if (iter++ > FILL_MAX_ITER) return;
      if (grid.scrollHeight <= grid.clientHeight && count < filtered.length) {
        setCount((c) => Math.min(c + STEP, filtered.length));
        requestAnimationFrame(fill);
      }
    };

    requestAnimationFrame(fill);
  }, [filtered.length, count]);

  const handleSearch = useCallback(() => {
    const isAll = norm(specialty) === norm(ALL_SPECIALTIES_VALUE);

    setApplied({
      q: norm(searchQuery),
      loc: norm(location),
      spec: isAll ? "" : norm(specialty),
    });
  }, [searchQuery, location, specialty]);

  const clearFilters = useCallback(() => {
    setSearchQuery("");
    setLocation("");
    setSpecialty(ALL_SPECIALTIES_VALUE);
    setApplied({ q: "", loc: "", spec: "" });
  }, []);

  const hasActive = !!(applied.q || applied.loc || applied.spec);
  const isEmpty = filtered.length === 0;

  return (
    <section className={`providers-list ${isRTL ? "is-rtl" : ""}`}>
      <header className="providers-header">
        <h1>{t("providers.title")}</h1>
        <h2>{t("providers.subtitle")}</h2>
      </header>

      <SearchBarGroup
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        location={location}
        setLocation={setLocation}
        specialty={specialty}
        setSpecialty={setSpecialty}
        onSearch={handleSearch}
      />

      {!isEmpty && (
        <div className="providers-results-bar">
          <span className="providers-count">
            {filtered.length} {t("providers.doctorsFound", "verified doctors found")}
          </span>
          {hasActive && (
            <button className="providers-clear-btn" onClick={clearFilters}>
              ✕ {t("providers.clearFilters") || "Clear filters"}
            </button>
          )}
        </div>
      )}

      {isEmpty ? (
        <div className="providers-empty">
          <div className="providers-empty-emoji">🩺</div>
          <div className="providers-empty-title">
            {hasActive
              ? t("providers.noResultsTitle") || "No verified doctors match your search"
              : t("providers.emptyTitle") || "No verified doctors available"}
          </div>
          {hasActive && (
            <button
              className="providers-clear-btn providers-clear-btn--centered"
              onClick={clearFilters}
            >
              ✕ {t("providers.clearFilters") || "Clear filters"}
            </button>
          )}
        </div>
      ) : (
        <div className="providers-grid" ref={gridRef}>
          {visible.map((doctor, i) => (
            <DoctorCard key={pickDocAnyId(doctor) ?? i} doctor={doctor} index={i} />
          ))}
          <div className="providers-footer" aria-hidden="true">
            {waiting && <div className="loading-spinner" />}
            <div ref={sentinelRef} style={{ height: 1 }} />
          </div>
        </div>
      )}
    </section>
  );
}

ProvidersList.propTypes = {
  doctors: PropTypes.oneOfType([PropTypes.array, PropTypes.object]),
};